/**
 * Read-only weergave van een pakket-snapshot (Epic 4 — Story 4.4, AC #3,
 * AD-5, UX-DR5/S5).
 *
 * Rendert UITSLUITEND uit `evaluations.package_snapshot` (het gepinde,
 * gedenormaliseerde JSON) — géén live join met `core_tasks`/
 * `assignment_periods`. Een latere catalogus-hernoeming verandert dus nooit
 * wat een afgeronde beoordeling toont (dit is de kern van FR-8). Scores zelf
 * (`core_task_scores`) blijven wél live opgehaald door de aanroepende
 * component — dit component toont alleen de pakket-structuur + chip.
 */

import type { PackageSnapshot } from '@functieloonhuis/shared';
import { PackageSnapshotChip } from './PackageSnapshotChip';
import { PACKAGE_LETTERS } from './wizard/periodLabels';

interface PackageSnapshotViewProps {
  snapshot: PackageSnapshot;
  finalizedAt: string | null;
}

function roundTo1(value: number): string {
  return (Math.round(value * 10) / 10).toFixed(1);
}

export function PackageSnapshotView({ snapshot, finalizedAt }: PackageSnapshotViewProps) {
  const isMultiPeriod = snapshot.periods.length >= 2;

  return (
    <div data-testid="package-snapshot-view" className="space-y-4">
      {finalizedAt && (
        <div>
          <PackageSnapshotChip finalizedAt={finalizedAt} />
        </div>
      )}

      <div className="space-y-6">
        {snapshot.periods.map((period, index) => {
          const weightPct = roundTo1(period.weight * 100);
          const endLabel = period.endDate ? `t/m ${period.endDate}` : '(lopend)';

          return (
            <div key={`${period.startDate}-${index}`} data-testid={`snapshot-period-${index}`} className="space-y-3">
              {isMultiPeriod && (
                <h3 className="text-sm font-semibold text-[#1E293B]">
                  Pakket {PACKAGE_LETTERS[index % PACKAGE_LETTERS.length]} — {period.startDate} {endLabel} &mdash; telde voor {weightPct}%
                </h3>
              )}
              <div className="space-y-2">
                {period.coreTasks.map((task) => (
                  <div
                    key={task.coreTaskId}
                    data-testid={`snapshot-core-task-${task.coreTaskId}`}
                    className="bg-white p-4 rounded-lg border border-[#E2E8F0]"
                  >
                    <p className="text-sm font-medium text-[#1E293B]">{task.name}</p>
                    <p className="text-xs text-gray-500">{task.domain} &middot; {task.timePercentage}%</p>
                  </div>
                ))}
                {period.exceptionMotivation && (
                  <p className="text-xs text-gray-500 italic">Motivatie 4e/5e kerntaak: {period.exceptionMotivation}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
